import { MailProviderKind } from '@affine/graphql';
import {
  MAIL_PROVIDER_CATALOG,
  type MailProviderId,
} from '@streamlain/mail-provider-contract';

export const CUSTOM_MAIL_PROVIDER_OPTION_ID = 'generic_imap_smtp';

export type MailProviderOptionId = Exclude<MailProviderId, 'custom_oauth2'>;

export interface MailPasswordProviderPreset {
  id: Exclude<
    MailProviderOptionId,
    'gmail_oauth' | 'microsoft_oauth' | typeof CUSTOM_MAIL_PROVIDER_OPTION_ID
  >;
  name: string;
  hint: string;
  domains: readonly string[];
  imapHost: string;
  imapPort: number;
  imapTls: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpTls: boolean;
}

export type MailProviderSetupHelpKind =
  | 'app_password_with_2fa'
  | 'custom_username'
  | 'imap_activation_and_app_password'
  | 'none'
  | 'separate_mail_password';

export interface MailProviderSetupHelp {
  kind: MailProviderSetupHelpKind;
  officialHelpUrl?: string;
}

export const MAIL_PASSWORD_PROVIDER_PRESETS: readonly MailPasswordProviderPreset[] =
  MAIL_PROVIDER_CATALOG.flatMap(profile => {
    if (!profile.endpoints) return [];
    return [
      {
        id: profile.id as MailPasswordProviderPreset['id'],
        name: profile.name,
        hint: profile.hint,
        ...profile.endpoints,
      },
    ];
  });

export const MAIL_PROVIDER_SETUP_HELP: Readonly<
  Record<MailPasswordProviderPreset['id'], MailProviderSetupHelp>
> = {
  gmx: {
    kind: 'imap_activation_and_app_password',
    officialHelpUrl:
      'https://hilfe.gmx.net/email/einstellungen/pop3-imap-einschalten.html',
  },
  web_de: {
    kind: 'imap_activation_and_app_password',
    officialHelpUrl: 'https://hilfe.web.de/pop-imap/einschalten.html',
  },
  mailbox_org: {
    kind: 'app_password_with_2fa',
    officialHelpUrl:
      'https://kb.mailbox.org/de/business/sicherheit-privatsphaere/e-mail-passwort/',
  },
  posteo: {
    kind: 'separate_mail_password',
    officialHelpUrl:
      'https://posteo.de/hilfe/zusaetzlichen-postfachschutz-aktivieren',
  },
  ionos: {
    kind: 'app_password_with_2fa',
    officialHelpUrl:
      'https://www.ionos.de/hilfe/e-mail/webmail-nutzen/e-mail-bestaetigung-in-zwei-schritten-aktivieren-und-konfigurieren/',
  },
  mail_de: {
    kind: 'custom_username',
    officialHelpUrl: 'https://mail.de/de/faq/erste_schritte',
  },
  freenet: {
    kind: 'none',
  },
  strato: {
    kind: 'none',
  },
};

export function getMailPasswordProviderPreset(
  id: MailProviderOptionId
): MailPasswordProviderPreset | null {
  return (
    MAIL_PASSWORD_PROVIDER_PRESETS.find(preset => preset.id === id) ?? null
  );
}

export function getMailProviderSetupHelp(
  id: MailProviderOptionId
): MailProviderSetupHelp | null {
  if (
    id === 'gmail_oauth' ||
    id === 'microsoft_oauth' ||
    id === CUSTOM_MAIL_PROVIDER_OPTION_ID
  ) {
    return null;
  }
  return MAIL_PROVIDER_SETUP_HELP[id];
}

export function findMailPasswordProviderPresetByEmail(
  emailAddress: string
): MailPasswordProviderPreset | null {
  const domain = emailAddress.trim().toLowerCase().split('@').at(-1);
  if (!domain) return null;
  return (
    MAIL_PASSWORD_PROVIDER_PRESETS.find(preset =>
      preset.domains.includes(domain)
    ) ?? null
  );
}

export function getMailProviderKindForOption(
  id: MailProviderOptionId
): MailProviderKind {
  const profile = MAIL_PROVIDER_CATALOG.find(profile => profile.id === id);
  if (!profile) throw new Error(`Unknown mail provider option: ${id}`);
  return MailProviderKind[profile.providerKind];
}
